/* Reading coordinates out of a tracker.
 *
 * A tracker has whatever columns the export had, so nothing here can assume a
 * shape. It finds the latitude and longitude by looking at the column names,
 * confirms them against the values, and then works out what to colour each
 * point by and how to group them into maps.
 *
 * Pure, and tested against the two real exports: the pole tracker's Site List
 * and the network live export, which name their columns differently and agree
 * on nothing except that a latitude is a number between -90 and 90.
 */

const LAT_NAMES = /^(lat|latitude|y|lat_dd|latitude_dd)$/i;
const LON_NAMES = /^(lon|lng|long|longitude|x|lon_dd|longitude_dd)$/i;

const num = (v) => {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (v === null || v === undefined || v === "") return null;
  const n = Number(String(v).trim());
  return Number.isFinite(n) ? n : null;
};

/* A column is a latitude if it is named like one and its values are in range.
 * The name alone is not enough: a Site List with a column called "Y" that
 * holds a pole height would put every device at the equator. */
function pick(columns, rows, names, limit) {
  const candidates = columns.filter((c) => names.test(String(c.label).trim().replace(/\s+/g, "")));
  for (const c of candidates) {
    const values = rows.map((r) => num(r[c.key])).filter((n) => n !== null);
    if (!values.length) continue;
    if (values.every((n) => Math.abs(n) <= limit)) return c;
  }
  return null;
}

/* Where a tracker's points are, if it has any. */
export function findCoordinates(columns = [], rows = []) {
  const lat = pick(columns, rows, LAT_NAMES, 90);
  const lon = pick(columns, rows, LON_NAMES, 180);
  return lat && lon ? { lat, lon } : null;
}

/* What to colour the points by: a column of short words the app recognises as
   states. The pole tracker calls it Status; another export might call it
   something else, so the values decide rather than the name. */
const STATE_WORDS = /^(complete|completed|done|closed|installed|in progress|started|open|pending|approved|submitted|not started|incomplete|scheduled|planned|blocked|on hold)$/i;

/* What a state field says, in this app's words.
 *
 * A GIS writes "In-Progress" and a spreadsheet writes "In progress", and a
 * field nobody has filled in comes back as 0 rather than as nothing, which is
 * the client's way of saying not recorded and not the same thing as not
 * started. Reading them as the same would report progress that has not
 * happened. */
export function stateLabel(value) {
  const raw = String(value ?? "").trim();
  if (!raw || raw === "0") return "Not reported";
  const spaced = raw.replace(/[-_]+/g, " ").replace(/\s+/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

/* How much of a column reads as a state. A blank, and a GIS zero, count as
   neither for nor against: they say the field exists and is empty. */
function stateShare(column, rows) {
  let hits = 0;
  let filled = 0;
  for (const r of rows) {
    const v = r[column.key];
    if (v === null || v === undefined || String(v).trim() === "" || String(v).trim() === "0") continue;
    filled += 1;
    if (STATE_WORDS.test(stateLabel(v))) hits += 1;
  }
  return filled ? { share: hits / filled, filled } : null;
}

export function findStatusColumn(columns = [], rows = []) {
  let best = null;
  for (const c of findStatusColumns(columns, rows)) {
    const seen = stateShare(c, rows);
    if (seen && (!best || seen.share > best.share)) best = { column: c, ...seen };
  }
  return best?.column ?? null;
}

/* Every column that reads as a state, not just the strongest one.
 *
 * A pole tracker has one. VIDE's schools layer has five, because a school
 * moves through five reviews before anybody breaks ground, and which of them
 * you want to see the island coloured by depends on the meeting you are in. */
export function findStatusColumns(columns = [], rows = []) {
  const found = [];
  for (const c of columns) {
    if (c.type === "number" || c.type === "date") continue;
    const seen = stateShare(c, rows);
    if (seen && seen.share >= 0.8) found.push({ column: c, ...seen });
  }
  /* Left in the layer's own column order, which on the schools layer is the
     order a school moves through the reviews. */
  return found.map((f) => f.column);
}

/* What to split the maps by. A program in the Virgin Islands is three
   islands, and one map of all three is a map of open water with specks at the
   edges. So the points are grouped by the column that names the place. */
const REGION_NAMES = /^(island|district|region|area|zone|county|parish|borough|sector)$/i;

export function findRegionColumn(columns = [], rows = []) {
  const named = columns.filter((c) => REGION_NAMES.test(String(c.label).trim()));
  for (const c of named) {
    const distinct = new Set(rows.map((r) => r[c.key]).filter(Boolean).map((v) => String(v).trim()));
    if (distinct.size >= 1 && distinct.size <= 12) return c;
  }
  return null;
}

/* What to call a point when somebody hovers it.
 *
 * The first text column is usually the island, and a tooltip that says
 * "St. John" over every point on the St. John map says nothing at all. What is
 * wanted is the thing's own id, and an id is the column whose value is
 * different on every row. */
export function findLabelColumn(columns = [], rows = []) {
  const texty = columns.filter((c) => c.type === "text" || c.type === "choice");
  for (const c of texty) {
    const values = rows.map((r) => r[c.key]).filter((v) => v !== null && v !== undefined && v !== "");
    if (values.length && new Set(values.map(String)).size === values.length) return c;
  }
  return texty[0] ?? null;
}

/* Everything a map needs, from a tracker's own columns and rows.
 *
 * The distinction that matters here, and the one that was wrong first time:
 * a site with no coordinates is not a site that does not exist. On this
 * program the plan lists every point that has to be built and the export
 * carries only the ones somebody has been out to, so most of the plan has no
 * coordinates yet and that is the whole story.
 *
 * So the progress is counted over every site, and only the ones with
 * coordinates are drawn. St. Croix with 71 planned and none built reads as
 * 0 of 71 with an empty map, which is true, rather than being left off
 * altogether, which would quietly flatter the program.
 */
export function mapModel(columns = [], rows = [], opts = {}) {
  const coords = findCoordinates(columns, rows);
  if (!coords) return null;

  const statusCol = opts.statusKey === null ? null
    : (columns.find((c) => c.key === opts.statusKey) ?? findStatusColumn(columns, rows));
  const regionCol = opts.regionKey === null ? null
    : (columns.find((c) => c.key === opts.regionKey) ?? findRegionColumn(columns, rows));
  const labelCol = columns.find((c) => c.key === opts.labelKey)
    ?? findLabelColumn(columns, rows);

  const sites = rows.map((row, i) => {
    const lat = num(row[coords.lat.key]);
    const lon = num(row[coords.lon.key]);
    const region = regionCol ? String(row[regionCol.key] ?? "").trim() : "";
    return {
      id: row._id ?? `row-${i}`,
      lat, lon,
      placed: lat !== null && lon !== null,
      label: labelCol ? String(row[labelCol.key] ?? "") : "",
      status: statusCol ? (row[statusCol.key] ?? null) : null,
      region: region || (regionCol ? "Unassigned" : "All sites"),
      row,
    };
  });

  const placed = sites.filter((s) => s.placed);

  const byRegion = new Map();
  for (const s of sites) {
    if (!byRegion.has(s.region)) byRegion.set(s.region, []);
    byRegion.get(s.region).push(s);
  }

  /* Ordered by how much work each place carries, so the island with the most
     left to do is the one you see without scrolling. */
  const regions = [...byRegion.entries()]
    .map(([name, all]) => {
      const points = all.filter((s) => s.placed);
      return {
        name, all, points,
        awaiting: all.length - points.length,
        ...progressOf(all),
        bounds: boundsOf(points),
      };
    })
    .sort((a, b) => b.total - a.total);

  return {
    coords, statusCol, regionCol, labelCol,
    sites,
    points: placed,
    placed: placed.length,
    missing: sites.length - placed.length,
    regions,
    ...progressOf(sites),
  };
}

/* The words every export the team has uses for finished. A status column is
   the client's own vocabulary, so this reads a handful of spellings rather
   than insisting on one. */
const DONE = /^(complete|completed|done|closed|installed|accepted|approved)$/i;
export const isDonePoint = (p) => DONE.test(String(p.status ?? "").trim());

function progressOf(sites) {
  const done = sites.filter(isDonePoint).length;
  return {
    total: sites.length,
    done,
    percent: sites.length ? Math.round((done / sites.length) * 100) : null,
  };
}

/* The rectangle that holds every point, with a little air around it so nothing
   sits against the edge of the frame. */
export function boundsOf(points) {
  if (!points.length) return null;
  const lats = points.map((p) => p.lat);
  const lons = points.map((p) => p.lon);
  const pad = 0.006;
  return [
    [Math.min(...lats) - pad, Math.min(...lons) - pad],
    [Math.max(...lats) + pad, Math.max(...lons) + pad],
  ];
}
